import { useCallback } from "react";
import {
  atom,
  atomFamily,
  selector,
  useResetRecoilState,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import FacebookPixel from "react-facebook-pixel";
import { DateTime } from "luxon";
import { useAuthUser } from "react-auth-kit";
import { axios } from "../lib/request";
import { dashboardQueryKey, gameUri, gameBrandingUri, leaderboardQueryKey } from "../lib/refresh";
import { DEFAULT_TIMEZONE } from "../consts/timezone";
import { replaceStagingBucketsUrl } from "../lib/media";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import { getErrorMessage } from "../lib/error";

export const errorState = atomFamily({
  key: "game/error",
  default: [],
});

const gameState = atom({
  key: "game",
  default: selector({
    key: "game/default",
    get: () => {
      return {
        assets: { photos: [] },
        category: "Active",
        game_type: 1,
        license_type: 1,
        photo_thumb: "",
        players: [],
        signed_legal: 1,
        tour: [],
        tz: DEFAULT_TIMEZONE,
      };
    },
  }),
});

export const fetchGame = async (game_id, game) => {
  let result = {}
  try {
    if (game_id === "new") {
      return { game };
    }
  
    const url = gameUri(game_id);
    const { data } = await axios.get(url);
    result = data

    if (result.game.reel) {
        result.game.reel = replaceStagingBucketsUrl(result.game.reel)
    }

    if( result.game.task_reels ) {
        result.game.task_reels = result.game.task_reels.map(reel => {
            reel.url = replaceStagingBucketsUrl(reel.url)
            return reel
        })
    }

    if( result.game.team_reels ) {
        result.game.team_reels = result.game.team_reels.map(reel => {
            reel.url = replaceStagingBucketsUrl(reel.url)
            return reel
        })
    }
  } catch (err) {
    // Re-throw so react-query can surface the error to ErrorBoundaries
    throw err
  }
  return result;
};

const flattenMainMenu = (mainMenu = {}) => {
  const flattened = {};

  for (const [menuKey, menuItem] of Object.entries(mainMenu)) {
    if (typeof menuItem === 'object' && menuItem !== null) {
      for (const [subKey, value] of Object.entries(menuItem)) {
        flattened[`${menuKey}_${subKey}`] = value;
      }
    } else {
      // Handle non-object values (defensive coding)
      flattened[menuKey] = menuItem;
    }
  }

  return flattened;
};

const reverseFlattenMainMenu = (flattened = {}) => {
  const mainMenu = {};
  const otherFields = {};

  for (const [key, value] of Object.entries(flattened)) {
    // Check if the key contains an underscore, indicating it's a nested field
    if (key.includes('_')) {
      const [menuKey, subKey] = key.split('_');
      
      // Initialize the menu object if it doesn't exist
      if (!mainMenu[menuKey]) {
        mainMenu[menuKey] = {};
      }
      
      // Add the subkey-value pair to the menu object
      mainMenu[menuKey][subKey] = value;
    } else {
      // Keep non-nested fields in the root object
      otherFields[key] = value;
    }
  }

  return {
    ...otherFields,
    main_menu: mainMenu
  };
};

const deepMerge = (target, source) => {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] instanceof Object && key in target && target[key] instanceof Object) {
      result[key] = deepMerge(target[key], source[key]);
    } else {
      result[key] = source[key];
    }
  }
  
  return result;
};

export const fetchBranding = async (game_id) => {
  const { data } = await axios.get(gameBrandingUri(game_id));

  const flattenedMainMenu = flattenMainMenu(data.main_menu);
  return {
    ...data,
    ...flattenedMainMenu,
    flattened_keys: Object.keys(flattenedMainMenu),
  };
};

export const fetchTimezone = async () => {
  const { data } = await axios.get(process.env.REACT_APP_API_TIMEZONE_LIST_URL);
  return data;
};

export const useTimezoneDataQuery = () => {
  const setAlert = useSetRecoilState(alertState);
  const { data } = useQuery('timezone', () => fetchTimezone(), {
    suspense: true,
    useErrorBoundary: false,
    onError: (error) => {
      const message = getErrorMessage(error);
      setAlert({ type: "error", message, closable: true });
    },
  });
  return data;
};

const createGame = async (params) =>
  axios.post(process.env.REACT_APP_API_GAME_CREATE_URL, params);

const updateGame = async (game_id, rawParams) => {
  let { assets, tour, ...params } = rawParams;

  if (params.start_at) {
    params = {
      ...params,
      start_at: DateTime.fromISO(params.start_at)
        .toFormat("MM/dd/yyyy hh:mm a")
        .toLocaleLowerCase(),
    };
  }

  if (params.finish_at) {
    params = {
      ...params,
      finish_at: DateTime.fromISO(params.finish_at)
        .toFormat("MM/dd/yyyy hh:mm a")
        .toLocaleLowerCase(),
    };
  }

  if (Object.keys(params).length === 0) {
    return;
  }

  return axios.put(
    `${process.env.REACT_APP_API_GAME_UPDATE_URL}/${game_id}.json`,
    params
  );
};

const updateGameBranding = async (game_id, rawParams) => {
  const { assets, tour, ...params } = rawParams;

  if (Object.keys(params).length) {
    return axios.put(
      `${process.env.REACT_APP_API_GREY_URL}/${game_id}/grey.json`,
      params
    );
  }
};

export const useGameDataQuery = (game_id) => {
  const game = useRecoilValue(gameState);
  const setAlert = useSetRecoilState(alertState);
  return useQuery(gameUri(game_id), () => fetchGame(game_id, game), {
    suspense: true,
    useErrorBoundary: false,
    onError: (error) => {
      const message = getErrorMessage(error);
      setAlert({ type: "error", message, closable: true });
    },
  });
};

export const useGameBrandingDataQuery = () => {
  const { game_id } = useParams();
  const { data: gameData } = useGameDataQuery(game_id);
  const game = gameData?.game || {}
  const setAlert = useSetRecoilState(alertState);
  return useQuery(gameBrandingUri(game_id), () => fetchBranding(game_id), {
    suspense: true,
    useErrorBoundary: false,
    onError: (error) => {
      const message = getErrorMessage(error);
      setAlert({ type: "error", message, closable: true });
    },
    keepPreviousData: true,
    select: (data) => ({
      ...data,
      assets: game?.assets || {},
      watermark: game?.watermark || {},
      top_nav_txt_color: data?.top_nav_text_color || null,
      body_high_color: data?.body_highlight_color || null,
      body_txt_color: data?.body_text_color || null,
    }),
  });
};

export const useGameQuery = (id) => {
  const params = useParams();
  const game_id = id || params.game_id;
  const { data } = useGameDataQuery(game_id);
  const [game, setGame] = useRecoilState(gameState);
  const { update } = useUpdateMutation(game_id);
  const queryClient = useQueryClient();
  const auth = useAuthUser()()
  
  const setter = useCallback(
    (params) => {
      if (game_id === "new") {
        // If user has not filled in the primary email, 
        // fallback to the current user's email
        const { primary_email } = params
        if (!primary_email) {
          params.primary_email = auth?.user_id
        }
        setGame({ ...game, ...params });
        queryClient.setQueryData(gameUri("new"), {
          ...data,
          game: { ...game, ...params },
        });
      } else {
        if (params?.timer_visible) {
            params.timer_visible = params.timer_visible === 'true'
                ? 1
                : 0
        }
        update(params);
      }
    },
    [data, game_id, game, queryClient, setGame, update, auth?.user_id]
  );

  return [data?.game || {}, setter];
};

export const useGameBrandingQuery = () => {
  const { game_id } = useParams();
  const { data } = useGameBrandingDataQuery(game_id);
  const { update } = useUpdateBrandingMutation();

  return [data, update];
};

export const useCreateMutation = () => {
  const game = useRecoilValue(gameState);
  const resetGame = useResetRecoilState(gameState);
  const setError = useSetRecoilState(errorState("new"));
  const queryClient = useQueryClient();
  
  const payload = {...game}
  if (payload?.start_anytime === undefined) {
    payload.start_at = undefined
    payload.finish_at = undefined
    payload.tz = undefined
  } else {
    payload.start_at = DateTime.fromISO(payload.start_at)
      .toFormat("MM/dd/yyyy hh:mm a")
      .toLocaleLowerCase()
    payload.finish_at = DateTime.fromISO(payload.finish_at)
      .toFormat("MM/dd/yyyy hh:mm a")
      .toLocaleLowerCase()
  }

  const { isLoading, mutateAsync } = useMutation(() => {
    // Validate required fields before API call to improve UX
    const errors = [];

    // Game name is required
    if (!payload.name?.trim()) {
      errors.push("Name cannot be blank.");
    }

    // Game description/preview is required
    if (!payload.description?.trim()) {
      errors.push("Preview cannot be blank."); 
    }

    // Primary email is required
    if (!payload.primary_email?.trim()) {
      errors.push("Primary email cannot be blank.");
    }

    // Game mode (start_anytime) validation - must be selected
    if (payload.start_anytime === undefined) {
      errors.push("Game Mode must be selected.");
    }

    // Entry code validation - required and min length
    if (!payload.code) {
      errors.push("Unlock code cannot be blank.");
    } else if (payload.code.length < 3) {
      errors.push("Unlock code is too short (min is 3 characters).");
    }

    // Show validation errors if any
    if (errors.length > 0) {
      const error = new Error();
      error.response = { data: errors };
      throw error;
    }

    return createGame(payload);
  }, {
    onError: (error) => {
      setError(error.response.data);
      window.scrollTo(0, 0);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(dashboardQueryKey());
      setError([]);
      resetGame();
    },
  });

  return { isCreating: isLoading, create: mutateAsync };
};

export const useUpdateMutation = (game_id) => {
  const setError = useSetRecoilState(errorState(game_id));
  const queryClient = useQueryClient();
  const uri = gameUri(game_id);

  const { mutateAsync } = useMutation((params) => updateGame(game_id, params), {
    onError: (error) => {
      setError(error.response.data);
      window.scrollTo(0, 0);
    },
    onMutate: (params) => {
      const data = queryClient.getQueryData(uri);
      setError([]);
      queryClient.setQueryData(uri, {
        ...data,
        game: { ...data.game, ...params },
      });
    },
    onSuccess: (_, params) => {
      if (!params.assets && !params.tour) {
        queryClient.invalidateQueries(uri);
      }
    },
  });

  return { update: mutateAsync };
};

export const useUpdateBrandingMutation = () => {
  const { game_id } = useParams();
  const { data } = useGameBrandingDataQuery(game_id);
  const setError = useSetRecoilState(errorState(game_id));
  const queryClient = useQueryClient();
  const uri = gameBrandingUri(game_id);

  const { mutateAsync } = useMutation(
    (params) => updateGameBranding(game_id, params),
    {
      onError: ({ response }) => {
        setError(response?.data?.error || response?.data);
        window.scrollTo(0, 0);
      },
      onMutate: (params) => {
        // Convert flattened params back to nested structure
        const paramsKeys = Object.keys(params)
        let nestedParams = {}

        // Only flatten params that are in the flattened_keys (main_menu)
        if (paramsKeys.some(key => data.flattened_keys.includes(key))) {
          nestedParams = reverseFlattenMainMenu(params)
        }
        
        // Only update main_menu with the new changes, preserve all other properties from original data
        const updatedData = {
          ...data,  // Keep all original data
          main_menu: deepMerge(
            data.main_menu || {},
            nestedParams.main_menu || {}
          ),
        };
        
        queryClient.setQueryData(uri, updatedData);
      },
    }
  );

  return { update: mutateAsync };
};

export const usePayMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const uri = gameUri(game_id);

  const { isLoading, mutateAsync } = useMutation(
    (params) =>
      axios.patch(
        `${process.env.REACT_APP_API_PAYMENT_URL}/${game_id}/teams.json`,
        params
      ),
    {
      onError: (error) => console.log("[error]", error),
      onSuccess: () => {
        queryClient.invalidateQueries(uri);
        FacebookPixel.track("Purchase");
      },
    }
  );

  return { isPaying: isLoading, pay: mutateAsync };
};

export const useBrandingPayMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const uri = gameUri(game_id);

  const { isLoading, mutateAsync } = useMutation(
    (params) =>
      axios.patch(
        `${process.env.REACT_APP_API_PAY_GREY_URL}/${game_id}/branding.json`,
        params
      ),
    {
      onError: (error) => console.log("[error]", error),
      onSuccess: () => {
        queryClient.invalidateQueries(uri);
        FacebookPixel.track("Purchase");
      },
    }
  );

  return { isPaying: isLoading, pay: mutateAsync };
};

export const useReelPayMutation = () => {
  const { game_id } = useParams();
  const queryClient = useQueryClient();
  const uri = gameUri(game_id);

  const { isLoading, mutateAsync } = useMutation(
    (params) =>
      axios.patch(
        `${process.env.REACT_APP_API_PAY_REELS_URL}/${game_id}/reels.json`,
        params
      ),
    {
      onError: (error) => console.log("[error]", error),
      onSuccess: () => {
        queryClient.invalidateQueries(uri);
        FacebookPixel.track("Purchase");
      },
    }
  );

  return { isPaying: isLoading, pay: mutateAsync };
};

export const useEmptyGameMutation = () => {
    const queryClient = useQueryClient()
    const { game_id } = useParams()
    const { isLoading: isEmptyingGame, mutateAsync: emptyGame } = useMutation(
        () =>
          axios.delete(
            `${process.env.REACT_APP_API_EMPTY_URL}/${game_id}/empty.json`
          ),
        {
          onSuccess: () => {
            queryClient.invalidateQueries(leaderboardQueryKey(game_id));
            queryClient.invalidateQueries(["monitor"]);
            queryClient.invalidateQueries(["stream"]);
          },
        }
    )
    return {
        isEmptyingGame,
        emptyGame,
    }
}